'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { ArrowLeft, KeyRound, Mail, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export function LoginPage({
  needsClaim = false,
  errorMessage = null,
  registered = false,
}: {
  needsClaim?: boolean;
  errorMessage?: string | null;
  registered?: boolean;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setServerError('Correo o contraseña incorrectos.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/login/native', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Only EMAIL_NOT_VERIFIED is a controlled, non-leaking reason: it is
        // returned AFTER credentials were validated, so it never reveals
        // whether an account exists to someone with wrong credentials.
        const errorMessage = data.error === 'EMAIL_NOT_VERIFIED'
          ? 'Debes verificar tu correo antes de iniciar sesión.'
          : 'Correo o contraseña incorrectos.';
        setServerError(errorMessage);
        return;
      }

      const code = data.code;
      const state = data.state;
      if (!code || !state) {
        setServerError('No se pudo iniciar la autenticación. Intente de nuevo.');
        return;
      }

      const callbackUrl = new URL('/api/auth/callback/kawrium', window.location.origin);
      callbackUrl.searchParams.set('code', code);
      callbackUrl.searchParams.set('state', state);
      window.location.href = callbackUrl.toString();
    } catch {
      setServerError('Correo o contraseña incorrectos.');
    } finally {
      setLoading(false);
    }
  }, [email, password]);

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      {/* Fondo oscuro premium relacionado con motocicletas */}
      <div className="absolute inset-0 -z-10 bg-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,#1a0b0a_0%,transparent_55%),radial-gradient(circle_at_90%_10%,#ff4500_8%,transparent_30%),radial-gradient(circle_at_20%_85%,#e31b23_10%,transparent_40%)]" />
      </div>

      <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          {/* Botón Atrás */}
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          <div className="text-center mb-8">
            <div className="inline-flex flex-col items-center gap-4 mb-4">
              <div className="relative">
                <img
                  src="/images/logo-live-moto.png"
                  alt="LIVE MOTO Logo"
                  className="w-40 h-auto object-contain drop-shadow-lg"
                />
              </div>
              <div className="text-center">
                <h1 className="text-3xl font-bold tracking-tight">
                  <span className="text-[#E31B23]">LIVE</span>{" "}
                  <span className="text-[#C0C0C0]">MOTO</span>
                </h1>
                <p className="text-[#FF4500] font-medium mt-1">Sistema de Gestión</p>
              </div>
            </div>
          </div>

          {registered ? (
            <div
              role="status"
              className="mb-4 rounded-xl border border-emerald-900/60 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-300 flex items-start gap-3"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Cuenta creada. Revisa tu correo y verifica tu dirección antes de iniciar sesión.</span>
            </div>
          ) : null}

          {errorMessage ? (
            <div
              role="alert"
              className="mb-4 rounded-xl border border-red-900/60 bg-red-950/30 px-4 py-3 text-sm text-red-300 flex items-start gap-3"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          ) : null}

          {serverError ? (
            <div
              role="alert"
              className="mb-4 rounded-xl border border-red-900/60 bg-red-950/30 px-4 py-3 text-sm text-red-300 flex items-start gap-3"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{serverError}</span>
            </div>
          ) : null}

          <Card
            className="bg-slate-900/70 border border-[#FF4500]/50 rounded-2xl p-6 sm:p-8 shadow-2xl backdrop-blur-md"
          >
            <CardContent className="pb-2">
              <p className="text-slate-300 text-center text-sm mb-5">
                Ingresa a tu cuenta para continuar
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-slate-950/60 border-slate-700 text-slate-100 placeholder-slate-500 focus:border-[#FF4500] focus:ring-1 focus:ring-[#FF4500]"
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="block text-sm font-medium text-slate-300">
                      Contraseña
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-xs text-slate-500 hover:text-[#FF4500] transition-colors pr-1"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-slate-950/60 border-slate-700 text-slate-100 placeholder-slate-500 focus:border-[#FF4500] focus:ring-1 focus:ring-[#FF4500]"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-[#FF4500] to-[#E31B23] text-white font-semibold shadow-lg hover:from-[#FF6B35] hover:to-[#C0392B] focus-visible:ring-[#FF4500] focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                  disabled={loading}
                >
                  {loading ? 'Iniciando...' : 'Iniciar sesión'}
                </Button>

                <div className="text-left text-xs text-slate-400 mt-1">
                  ¿Olvidaste tu contraseña?
                  <Link
                    href="/forgot-password"
                    className="text-[#FF4500] hover:text-[#FF6B35] font-medium ml-1"
                  >
                    Restablecerla
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Identidad protegida por Kawrium (disclaimer infraestructura) */}
          <div className="mt-3 text-center text-slate-500 text-xs">
            Identidad protegida por Kawrium
          </div>
          <div className="mt-1 text-center text-slate-500 text-xs">
            Este sistema utiliza tecnología de Kawrium
          </div>

          {needsClaim ? (
            <Card className="mt-4 border-slate-800 bg-slate-900/40">
              <CardContent className="pt-5">
                <p className="text-sm text-slate-300">
                  Tu cuenta anterior en LiveMoto se conserva con su mismo rol. Necesitas
                  el codigo de reclamo que te entrego el taller.
                </p>
                <Button
                  asChild
                  className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-slate-700 text-white font-medium"
                >
                  <Link href="/claim">
                    <KeyRound className="w-4 h-4" />
                    Tengo un codigo de reclamo
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : null}

          <p className="mt-8 text-center text-slate-400 text-sm">
            ¿No tienes cuenta?{' '}
            <Link href="/register" className="text-[#FF4500] hover:text-[#FF6B35] font-semibold">
              Crear cuenta
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
